// The slider's value <-> position mapping (ACT2-569).
//
// Everything on these charts is positioned by VALUE — bars, handle, ticks, highlight spans, drag
// hit-testing — and they all route through ONE pair of functions. That is why a non-linear axis is a
// small change here and an impossible one anywhere else: swapping the pair swaps the axis for every
// shape at once, and nothing can drift out of agreement with anything else.
//
// The kind IS the backend's `BinScale`, imported: the bins were binned in this space at query time, so
// drawing them in any other space is the original defect (one tall bar and twenty-three minimum-height
// dashes), not a partial improvement.

import { type BinScale, scaleTransform } from '@server/binning-scale';

/**
 * The binning space, IMPORTED from the backend rather than restated.
 *
 * These two files must agree exactly: the bins were binned in this space at query time and are drawn in
 * it here, so a divergence lands handles and ticks off the bars they belong to — with every test on
 * either side still green, because neither one can see the other. It used to be a hand-copied union
 * plus a hand-copied `LOG_FLOOR` and a hand-copied transform table, held together by a comment saying
 * "same reason, same value". ACT2-569 added the `@server/binning-scale` alias to `ui/vite.config.ts`,
 * `ui/vitest.config.ts`, `.storybook/main.ts` and both tsconfigs, and `binning-scale.ts` imports
 * nothing at all — so the agreement can be a compiler's job instead of a reviewer's.
 */
export type AxisScaleKind = BinScale;

export interface AxisScale {
  /** Value -> percent along the track (0..100). Clamped, and never NaN. */
  pct: (v: number) => number;
  /** Fraction of the track (0..1) -> value. The exact inverse of `pct`. */
  valueAt: (fraction: number) => number;
  /** The keyboard/arrow step AT a value. Constant under linear; magnitude-relative under log. */
  stepAt: (v: number) => number;
  /** Round a value the USER placed onto a readable number for this axis. */
  snap: (v: number) => number;
}

/**
 * How far one arrow-key press moves in the BINNING space — i.e. how much of a natural log unit, so
 * ~5% of a decade divided by ln(10), or one twentieth of an e-fold.
 *
 * TUNING. A log axis has no single meaningful additive step — 1,000 is a rounding error at 400,000 and
 * eight whole bins at 125 — so the step is a constant distance ALONG THE TRACK instead, which on `log`
 * is a fixed fraction of the value. Lower it (finer steps, more presses to cross the track) if users
 * report overshooting; raise it if crossing the range by keyboard is reported as tedious. Shift+arrow
 * already multiplies by 10 in `SliderShell`.
 */
const LOG_STEP_FRACTION = 0.05;

/**
 * The coarsest snap grid allowed, as a fraction of the FRAME's own span.
 *
 * TUNING. Raise it toward 1 if committed thresholds come back looking unnecessarily precise; lower it
 * if the handle is reported as not settling on round numbers. Below ~0.02 the cap stops binding at all
 * on a wide frame, because two significant figures is already finer than that.
 */
const SNAP_MAX_SPAN_FRACTION = 0.1;

/**
 * Round to two significant figures — 127 -> 130, 383,703 -> 380,000. A marketer picks round numbers,
 * and on a log axis "round" means round at the value's OWN magnitude, not to a fixed grid.
 *
 * BOUNDED BY THE FRAME, not only by the value. Two significant figures of a number is a grid whose step
 * comes from that number's magnitude, and on a NARROW frame at a HIGH magnitude that step can be wider
 * than the whole chart: on `[1000, 1100]` the grid is 100, so every value on the track rounds to 1,000 —
 * the frame minimum. The user released the handle mid-track, watched it teleport to the far left, and
 * got 1,000 written into their rule. It was the one place a user action produced a number they did not
 * choose. Capping the grid at a fraction of the span keeps the round-number behaviour everywhere it was
 * already correct (the ACT2-569 column still snaps 6,980.7 -> 7,000) and only bites where it was wrong.
 */
function snapSignificant(v: number, span: number): number {
  if (!Number.isFinite(v) || v === 0) return v;
  const valueExp = Math.floor(Math.log10(Math.abs(v))) - 1;
  const cap = span * SNAP_MAX_SPAN_FRACTION;
  // A collapsed or non-finite frame has no opinion, so the value's own magnitude stands.
  const exp = cap > 0 ? Math.min(valueExp, Math.floor(Math.log10(cap))) : valueExp;
  const magnitude = Math.pow(10, exp);
  return Math.round(v / magnitude) * magnitude;
}

export function axisScale(
  kind: AxisScaleKind,
  min: number,
  max: number,
  linearStep: number,
  /**
   * The smallest change the column can actually WRITE — `domain.granularity`, or 1 on an integer
   * column. `0` (the default) means no floor.
   *
   * NOT `linearStep`. Flooring the log step at `linearStep` defeats the entire feature: `defaultDragStep`
   * returns 2,000 for the ACT2-569 column's span, so `Math.max(linearStep, v * 0.05)` hands back 2,000 at
   * every value below 40,000 — the coarse linear grid, on the log axis, at exactly the crowded end the
   * log axis was introduced to open up. The floor that IS needed is a different quantity: it stops an
   * arrow key proposing 8.4 on an integer column, and it is small.
   */
  minStep = 0,
): AxisScale {
  const t = scaleTransform(kind);
  const lowT = t.to(min);
  // `|| 1` for the same reason the old `span` had it: a collapsed frame must still map somewhere finite
  // rather than dividing by zero and painting every shape at NaN%.
  const spanT = t.to(max) - lowT || 1;
  const clamp = (v: number) => Math.min(max, Math.max(min, v));
  return {
    pct: (v) => ((t.to(clamp(v)) - lowT) / spanT) * 100,
    valueAt: (fraction) => clamp(t.from(lowT + fraction * spanT)),
    // The step is a constant advance in the TRANSFORMED space, converted back to a value delta. On
    // `log` that is the magnitude-relative step it has always been — `v * (e^0.05 - 1)`, within 3% of
    // the old `v * 0.05` — and on `log1p` it is the one that does not collapse.
    //
    // The old spelling was `Math.abs(v) * LOG_STEP_FRACTION`, which is exactly 0 at v = 0. Zero is not
    // an exotic input on a `log1p` axis: that scale is chosen PRECISELY because the column reaches
    // zero, so the frame starts there. On a float column (`minStep === 0`) the handle then could not be
    // moved off the low end by keyboard at all — press Home and no arrow press changes anything, a
    // control operable by mouse and not by keyboard. (a11y)
    stepAt: (v) => {
      if (kind === 'linear') return linearStep;
      const step = t.from(t.to(v) + LOG_STEP_FRACTION) - v;
      // The fallback is unreachable from any in-frame value (`from` is monotonic, so the delta is
      // positive) and exists so an overflowed or NaN input still yields a step that MOVES the handle.
      return Math.max(minStep, Number.isFinite(step) && step > 0 ? step : LOG_STEP_FRACTION);
    },
    // `minStep` applies to the DRAG too, not just the arrow keys: two significant figures of 8.4 is
    // 8.4, and committing that to an integer column writes a threshold no row can equal. Same floor,
    // same reason, applied after the significant-figure round so the round number wins where it can.
    snap: (v) => {
      if (kind === 'linear') return v;
      const snapped = snapSignificant(v, max - min);
      return minStep > 0 ? Math.round(snapped / minStep) * minStep : snapped;
    },
  };
}
